import React, { useCallback, useEffect, useMemo, useState } from "react";
import firebase from "firebase/app";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";

const POPULAR_TAG_LIMIT = 150;
const CAMPAIGN_CHUNK_SIZE = 500;
const USER_TAG_SUMMARY_URL =
  "https://getusertagsummary-650318705789.us-central1.run.app";
const SELECTED_TAG_EMAILS_URL =
  "https://getselectedtagemails-650318705789.us-central1.run.app";

const maskEmail = (value) => {
  const email = String(value || "").trim().toLowerCase();
  const atIndex = email.lastIndexOf("@");
  if (atIndex <= 0) return "*****";

  const localPart = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  const maskedLength = Math.max(5, Math.min(localPart.length - 1, 10));

  return `${localPart.charAt(0)}${"*".repeat(maskedLength)}@${domain}`;
};

const copyTextToClipboard = async (text) => {
  if (!text) return;

  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export default function AdminUserTags() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [d1TagCounts, setD1TagCounts] = useState({});
  const [selectedRealEmails, setSelectedRealEmails] = useState([]);
  const [maskedEmails, setMaskedEmails] = useState([]);
  const [selectedEmailCount, setSelectedEmailCount] = useState(0);
  const [loadingSelectedEmails, setLoadingSelectedEmails] = useState(false);
  const [selectedEmailError, setSelectedEmailError] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeGroupTag, setActiveGroupTag] = useState("");
  const [campaignSubject, setCampaignSubject] = useState("");
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignMessage, setCampaignMessage] = useState("");

  const checkAccess = useCallback(async () => {
    try {
      if (!currentUser) {
        navigate("/");
        return;
      }

      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setHasAccess(false);
        setCheckingAccess(false);
        return;
      }

      setHasAccess(true);
      setCheckingAccess(false);
    } catch (err) {
      console.error("Error checking admin access:", err);
      setError("Failed to verify admin access");
      setHasAccess(false);
      setCheckingAccess(false);
    }
  }, [currentUser, navigate]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const idToken = await currentUser.getIdToken();
      const tagSummaryResponse = await fetch(USER_TAG_SUMMARY_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const tagSummary = await tagSummaryResponse.json();
      if (!tagSummaryResponse.ok) {
        throw new Error(tagSummary?.message || "Failed to load tags from D1");
      }

      const nextTagCounts = {};
      (Array.isArray(tagSummary?.tags) ? tagSummary.tags : []).forEach((item) => {
        const tagName = String(item?.tagName || "").trim();
        if (tagName) {
          nextTagCounts[tagName] = Number(item?.qty || 0);
        }
      });

      setD1TagCounts(nextTagCounts);
    } catch (err) {
      console.error("Error loading users:", err);
      setError(err?.message || "Failed to load users");
      setD1TagCounts({});
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  useEffect(() => {
    if (hasAccess) {
      loadUsers();
    }
  }, [hasAccess, loadUsers]);

  const allTags = useMemo(() => {
    return Object.keys(d1TagCounts).sort((a, b) => a.localeCompare(b));
  }, [d1TagCounts]);

  const tagCounts = useMemo(() => {
    return d1TagCounts;
  }, [d1TagCounts]);

  const popularTags = useMemo(() => {
    return Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.tag.localeCompare(b.tag);
      })
      .slice(0, POPULAR_TAG_LIMIT);
  }, [tagCounts]);

  const tagGroups = useMemo(() => {
    return Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.tag.localeCompare(b.tag);
      });
  }, [tagCounts]);

  useEffect(() => {
    if (tagGroups.length === 0) {
      setActiveGroupTag("");
      return;
    }

    const activeExists = tagGroups.some((group) => group.tag === activeGroupTag);
    if (!activeGroupTag || !activeExists) {
      setActiveGroupTag(tagGroups[0].tag);
    }
  }, [activeGroupTag, tagGroups]);

  const groupedTagLabel =
    selectedTags.length > 0
      ? selectedTags.join(", ")
      : activeGroupTag;

  const tagsForEmailLookup = useMemo(() => {
    if (selectedTags.length > 0) return selectedTags;
    return activeGroupTag ? [activeGroupTag] : [];
  }, [activeGroupTag, selectedTags]);

  useEffect(() => {
    if (!currentUser || tagsForEmailLookup.length === 0) {
      setMaskedEmails([]);
      setSelectedRealEmails([]);
      setSelectedEmailCount(0);
      setSelectedEmailError("");
      return;
    }

    let cancelled = false;

    const loadSelectedEmails = async () => {
      setLoadingSelectedEmails(true);
      setSelectedEmailError("");

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(SELECTED_TAG_EMAILS_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tags: tagsForEmailLookup }),
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || "Failed to load selected tag emails");
        }

        if (!cancelled) {
          const realEmails = Array.from(
            new Set(
              (Array.isArray(data?.emails) ? data.emails : [])
                .map((email) => String(email || "").trim().toLowerCase())
                .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            )
          );

          setSelectedRealEmails(realEmails);
          setMaskedEmails(
            realEmails.map(maskEmail)
          );
          setSelectedEmailCount(realEmails.length);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading selected tag emails:", err);
          setMaskedEmails([]);
          setSelectedRealEmails([]);
          setSelectedEmailCount(0);
          setSelectedEmailError(
            err?.message || "Failed to load selected tag emails"
          );
        }
      } finally {
        if (!cancelled) setLoadingSelectedEmails(false);
      }
    };

    loadSelectedEmails();

    return () => {
      cancelled = true;
    };
  }, [currentUser, tagsForEmailLookup]);

  const copyPopularTags = async () => {
    if (!popularTags.length) return;
    await copyTextToClipboard(popularTags.map(({ tag }) => tag).join("\n"));
  };

  const createCampaign = async () => {
    const subject = campaignSubject.trim();
    if (!subject) {
      setCampaignMessage("Please enter a campaign subject.");
      return;
    }

    if (selectedRealEmails.length === 0) {
      setCampaignMessage("No grouped emails available for this campaign.");
      return;
    }

    try {
      setCreatingCampaign(true);
      setCampaignMessage("");

      const timestamp = firebase.firestore.FieldValue.serverTimestamp();
      const campaignRef = fires.collection("campaigns").doc();
      const chunks = [];

      for (let index = 0; index < selectedRealEmails.length; index += CAMPAIGN_CHUNK_SIZE) {
        chunks.push(selectedRealEmails.slice(index, index + CAMPAIGN_CHUNK_SIZE));
      }

      const batch = fires.batch();
      batch.set(campaignRef, {
        chunkCount: chunks.length,
        currentChunk: "",
        emailCount: selectedRealEmails.length,
        subject,
        timestamp,
      });

      chunks.forEach((emails, index) => {
        const chunkRef = campaignRef
          .collection("chunks")
          .doc(String(index + 1).padStart(3, "0"));

        batch.set(chunkRef, {
          count: emails.length,
          status: "pending",
          emails,
          timestamp,
        });
      });

      await batch.commit();
      setCampaignSubject("");
      setCampaignMessage(
        `Campaign created with ${chunks.length} chunks and ${selectedRealEmails.length} emails.`
      );
    } catch (err) {
      console.error("Error creating campaign:", err);
      setCampaignMessage(err?.message || "Failed to create campaign.");
    } finally {
      setCreatingCampaign(false);
    }
  };

  const clearSelectedTags = () => {
    setSelectedTags([]);
    setSelectedTag("all");
  };

  const setOnlySelectedTag = (tag) => {
    setActiveGroupTag(tag);
    setSelectedTags([tag]);
    setSelectedTag(tag);
  };

  const toggleSelectedTag = (tag) => {
    setActiveGroupTag(tag);
    setSelectedTags((previousTags) => {
      const nextTags = previousTags.includes(tag)
        ? previousTags.filter((item) => item !== tag)
        : [...previousTags, tag];

      if (nextTags.length === 0) {
        setSelectedTag("all");
      } else if (nextTags.length === 1) {
        setSelectedTag(nextTags[0]);
      } else {
        setSelectedTag("multi");
      }

      return nextTags;
    });
  };

  if (checkingAccess) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Verifying admin data access...</p>
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          You do not have permission to view this page. Admin access is required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ maxWidth: "1400px" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="mb-1">User Tags</h2>
          <div className="text-muted">
            Tag quantities loaded from D1; user details loaded from Firestore.
          </div>
        </div>
        <Button variant="outline-primary" onClick={loadUsers} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Select
                value={selectedTag}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "all" || value === "none") {
                    setSelectedTags([]);
                    setSelectedTag(value);
                    return;
                  }
                  setOnlySelectedTag(value);
                }}
              >
                <option value="all">All tags</option>
                <option value="none">No tags</option>
                {selectedTags.length > 1 && (
                  <option value="multi">Selected tags ({selectedTags.length})</option>
                )}
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag} ({tagCounts[tag] || 0})
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h6 className="mb-0">Top 150 Most Popular Tags</h6>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={copyPopularTags}
            disabled={popularTags.length === 0}
          >
            Copy top 150 tags
          </Button>
        </Card.Header>
        <Card.Body>
          {popularTags.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {popularTags.map(({ tag, count }, index) => (
                <Button
	                  key={tag}
	                  variant={selectedTags.includes(tag) ? "primary" : "outline-primary"}
	                  size="sm"
	                  onClick={() => toggleSelectedTag(tag)}
	                >
	                  #{index + 1} {tag} ({count})
	                </Button>
              ))}
            </div>
          ) : (
            <div className="text-muted">No tags found yet.</div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <h6 className="mb-0">Tag Groups</h6>
        </Card.Header>
        <Card.Body>
          {tagGroups.length > 0 ? (
            <Row>
              <Col lg={4} className="mb-3 mb-lg-0">
                <div className="border rounded" style={{ maxHeight: "420px", overflowY: "auto" }}>
                  {tagGroups.map(({ tag, count }) => (
                    <Button
                      key={tag}
	                      variant={selectedTags.includes(tag) ? "primary" : "light"}
	                      className="w-100 d-flex justify-content-between align-items-center rounded-0 border-0 border-bottom text-start"
	                      onClick={() => toggleSelectedTag(tag)}
	                    >
	                      <span>{tag}</span>
	                      <Badge bg={selectedTags.includes(tag) ? "light" : "secondary"} text={selectedTags.includes(tag) ? "dark" : undefined}>
	                        {count}
	                      </Badge>
	                    </Button>
                  ))}
                </div>
              </Col>
              <Col lg={8}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
	                  <div>
	                    <h6 className="mb-0">{groupedTagLabel || "No tag selected"}</h6>
	                  </div>
	                  <div className="d-flex flex-wrap gap-2">
	                    <Button
	                      variant="outline-secondary"
	                      size="sm"
	                      onClick={clearSelectedTags}
	                      disabled={selectedTags.length === 0}
	                    >
	                      Clear selected tags
	                    </Button>
	                  </div>
	                </div>
	                <Form.Group className="mb-3">
	                  <Form.Label className="small text-muted mb-1">
	                    {loadingSelectedEmails
	                      ? "Loading email count..."
	                      : `${selectedEmailCount} unique subscribed email${selectedEmailCount === 1 ? "" : "s"}`}
	                  </Form.Label>
	                  <Form.Control
	                    as="textarea"
	                    rows={6}
	                    readOnly
	                    value={maskedEmails.join("\n")}
	                    placeholder="No matching subscribed emails."
	                  />
	                </Form.Group>
	                {selectedEmailError && (
	                  <Alert variant="danger" className="py-2">
	                    {selectedEmailError}
	                  </Alert>
	                )}
	                <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
	                  <Form.Group style={{ minWidth: "280px", flex: "1 1 320px" }}>
	                    <Form.Label className="small text-muted mb-1">Campaign subject</Form.Label>
	                    <Form.Control
	                      value={campaignSubject}
	                      onChange={(e) => {
	                        setCampaignSubject(e.target.value);
	                        setCampaignMessage("");
	                      }}
	                      placeholder="Subject"
	                    />
	                  </Form.Group>
	                  <Button
	                    variant="success"
	                    onClick={createCampaign}
	                    disabled={creatingCampaign || selectedRealEmails.length === 0}
	                  >
	                    {creatingCampaign ? "Creating..." : "Create campaign"}
	                  </Button>
	                </div>
	                {campaignMessage && (
	                  <Alert
	                    variant={campaignMessage.startsWith("Campaign created") ? "success" : "warning"}
	                    className="py-2"
	                  >
	                    {campaignMessage}
	                  </Alert>
	                )}
              </Col>
            </Row>
          ) : (
            <div className="text-muted">No tag groups found yet.</div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
